"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleQuestionMark as HelpCircle, PhoneCall, ArrowRight, ShieldCheck } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const faqs = [{
  question: "Do BMF Scaffolding cover High Wycombe?",
  answer: "Yes. BMF Scaffolding provides complete scaffolding services across High Wycombe and surrounding areas in South Buckinghamshire, including residential streets, town centre locations, and industrial estates."
}, {
  question: "What types of scaffolding can you provide in High Wycombe?",
  answer: "We supply and erect independent access scaffolding, chimney stacks, temporary roofs, edge protection, birdcages, and tailored commercial systems. All structures comply with TG20 guidelines and current safety standards."
}, {
  question: "Can you provide scaffolding for roof work?",
  answer: "Yes. We regularly install scaffolding for re-roofing, solar panel installation, chimney repairs, and gutter maintenance, ensuring compliant edge protection and safe working platforms."
}, {
  question: "Do you work with contractors and developers?",
  answer: "Yes. We partner with main contractors, developers, roofing contractors, solar installers, facilities managers, and local authorities, holding NASC and CHAS accreditations."
}, {
  question: "Can scaffolding be installed where access is restricted?",
  answer: "Yes. Our scaffolders assess tight alleys, narrow access passages, sloping ground, and pedestrian pathways. We design and erect custom access arrangements to accommodate specific site constraints."
}, {
  question: "How do I request a quote in High Wycombe?",
  answer: "You can submit your project details and site drawings through our online quote request form or contact our team directly. We evaluate your requirements and provide a clear, itemised quotation based on your site specifications."
}];
export default function WycombeFaq() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="highwycombefaq" data-nav="light" className="bg-background py-16 md:py-24 border-t border-border">
      <div className="container mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <Badge variant="outline" className="mb-4 gap-1.5 px-3 py-1 text-xs">
            <HelpCircle className="size-3.5 text-primary" />
            High Wycombe coverage
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-foreground font-default mb-4">
            Frequently asked questions about scaffolding in High Wycombe
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg">
            Straightforward details on local coverage, site access constraints, and scaffolding setups across South Buckinghamshire.
          </p>
        </motion.div>

        {/* FAQ Accordion */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.1,
        ease: "easeOut"
      }} className="bg-card rounded-xl border border-border p-6 sm:p-8 shadow-sm">
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, i) => <AccordionItem key={faq.question} value={`item-${i}`} data-index={i} className="border-border px-1 py-1">
                <AccordionTrigger className="text-base sm:text-lg font-medium text-foreground hover:no-underline hover:text-primary transition-colors text-left py-4">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-sm sm:text-base leading-relaxed pt-1 pb-4">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </motion.div>

        {/* Bottom CTA / Verification strip */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.2
      }} className="mt-10 rounded-xl bg-accent/40 border border-border p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3 text-left">
            <div className="rounded-md bg-background p-2 border border-border shrink-0">
              <ShieldCheck className="size-5 text-primary" />
            </div>
            <div>
              <p className="text-sm font-medium text-foreground">
                NASC &amp; CHAS accredited
              </p>
              <p className="text-xs text-muted-foreground">
                Committed to standard-compliant site safety on every scaffold build.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 w-full sm:w-auto shrink-0">
            <Button asChild variant="outline" size="sm" className="w-full sm:w-auto">
              <Link to="/AreasWeCoverPage">
                All areas covered
              </Link>
            </Button>
            <Button asChild size="sm" className="w-full sm:w-auto gap-1.5">
              <Link to="/ContactRequestQuotePage">
                Request a quote
                <ArrowRight className="size-3.5" />
              </Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}